import {
  ArrowForwardOutlined as ArrowForwardIcon,
  HomeOutlined as HomeOutlinedIcon,
  Inventory2Outlined as InventoryOutlinedIcon,
} from "@/components/icons";
import * as React from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { useMe } from "@/api/hooks";
import { brand } from "@/theme";

/** Catch-all for unknown routes.
 *
 * Before this, ``/*`` redirected blindly to ``/app`` which redirected
 * to ``/login`` for unauth'd readers. A stale URL in a wiki or a
 * typo in a Slack message vanished into a login wall with zero
 * hint at what the user was trying to reach. Now: a friendly page
 * that names the three places the user most likely wanted to be
 * (sessions sidebar, document library, landing page) so they can
 * recover in one click.
 */
export function NotFoundPage(): React.JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  // Signed-in readers likely wanted a specific session / document;
  // unauth'd readers likely wanted the landing or login. Surface
  // the right primary action for each, but always show both.
  const authed = Boolean(me.data);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: brand.background,
        display: "flex",
        alignItems: "center",
        py: 8,
      }}
    >
      <Container maxWidth="sm">
        <Stack sx={{ gap: 3, textAlign: "center" }}>
          <Typography
            variant="overline"
            sx={{ color: brand.muted, letterSpacing: 1.6 }}
          >
            404
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 600 }}>
            {t("errors.not_found_title", "That page doesn't exist.")}
          </Typography>
          <Typography color="text.secondary">
            {t("errors.the_url", "The URL")}{" "}
            <Box
              component="code"
              sx={{
                fontFamily: brand.fonts.mono,
                px: 0.75,
                py: 0.25,
                borderRadius: 0.5,
                bgcolor: brand.ljusgra,
              }}
            >
              {location.pathname}
            </Box>{" "}
            {t(
              "errors.not_found_body",
              "didn't match any route. It may have been renamed, or the link was mistyped.",
            )}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{ gap: 1.5, justifyContent: "center", mt: 2 }}
          >
            {authed ? (
              <>
                <Button
                  href="/app/sessions"
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                >
                  {t("errors.go_to_sessions", "Go to sessions")}
                </Button>
                <Button
                  href="/app/documents"
                  variant="outlined"
                  startIcon={<InventoryOutlinedIcon />}
                >
                  {t("errors.open_library", "Open the library")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  href="/"
                  variant="contained"
                  startIcon={<HomeOutlinedIcon />}
                >
                  {t("errors.back_to_landing", "Back to the landing page")}
                </Button>
                <Button href="/login" variant="outlined">
                  {t("errors.sign_in", "Sign in")}
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}
